import { useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Dialog } from "@radix-ui/themes";
import { LockClosedIcon } from '@radix-ui/react-icons';

import { FoodyButton } from '@app/design-system/components/FoodyButton';
import { FormField, Input } from "@app/design-system/components/FormField";
import { useStoredUser } from "@app/store/selectors";
import { setUser } from "@app/store/user.store";
import UserService from "@app/services/user.service";
import { passwordRegex } from "@app/utils/validations";
import { hideGlobalModal } from "@app/utils/modals";

import Loader from "@app/components/ui/loader";

export default function PasswordResetModalComponent() {
    const dispatch = useDispatch();
    const storedUser = useStoredUser();
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);

    const { register, handleSubmit, clearErrors, watch, formState: { errors }, setError } = useForm({
        reValidateMode: 'onSubmit',
        shouldFocusError: true,
        criteriaMode: 'firstError',
    });

    const onSubmit = (data: any) => {
        clearErrors();
        setIsLoading(true);

        UserService
            .forcePasswordChange(data)
            .then(() => {
                dispatch(setUser({ ...storedUser, forcePassReset: false }));
                hideGlobalModal('passReset');
            })
            .catch(err => {
                let name = 'newPass';
                let msg = t('INVALID_PASS_PLACEHOLDER');

                if (err.status === 403) {
                    msg = t('INVALID_PASS');
                } else if (err.status === 405) {
                    name = 'newPassConfirm';
                    msg = t('INVALID_IDENTICAL_PASS_PLACEHOLDER');
                }

                setError(name, { type: 'validate', message: msg });
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <Dialog.Root open={true}>
            <Dialog.Content maxWidth="480px" className="fdy-modal-login" data-test="force-password-modal">
                <div className="modal_header bg_gray text-center w-100 py-3 m-0">
                    <Dialog.Title size="4" className="m-0">{t('Reset password')}</Dialog.Title>
                </div>
                <div id="sign-in-dialog" className="zoom-anim-dialong py-4">
                    <p className={`text-center`}>{t('FORCE_PASS_RESET_PLACEHOLDER')}</p>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="sign-in-wrapper">
                            <FormField
                                label={t('YOUR_NEW_PASSWORD_PLACEHOLDER')}
                                htmlFor="newPass"
                                error={errors['newPass']?.message as string}
                                icon={<LockClosedIcon />}>
                                <Input
                                    data-test="force-password-new"
                                    type="password"
                                    name="newPass"
                                    id="newPass"
                                    autoCapitalize="off"
                                    autoComplete="off"
                                    spellCheck="true"
                                    {...register('newPass', {
                                        required: t('Полето е задължително'),
                                        pattern: process.env.ENV === 'production' ? {
                                            value: passwordRegex,
                                            message: t('INVALID_PASS')
                                        } : null
                                    })} />
                            </FormField>
                            <FormField
                                label={t('YOUR_NEW_PASSWORD_CONFIRM_PLACEHOLDER')}
                                htmlFor="newPassConfirm"
                                error={errors['newPassConfirm']?.message as string}
                                icon={<LockClosedIcon />}>
                                <Input
                                    data-test="force-password-confirm"
                                    type="password"
                                    name="newPassConfirm"
                                    id="newPassConfirm"
                                    autoCapitalize="off"
                                    autoComplete="off"
                                    spellCheck="true"
                                    {...register('newPassConfirm', {
                                        required: t('Полето е задължително'),
                                        validate: (val: string) => {
                                            if (watch('newPass') !== val) {
                                                return t('PASS_NOT_MATCH');
                                            }
                                        }
                                    })} />
                            </FormField>
                            <Loader loading={isLoading}>
                                <div className="text-center">
                                    <FoodyButton data-test="force-password-submit" type="submit" fullWidth className="mb_5">{t('Смени')}</FoodyButton>
                                </div>
                            </Loader>
                        </div>
                    </form>
                </div>
            </Dialog.Content>
        </Dialog.Root>
    );
}